<template>
  <div class="container-fluid p-3 personalized-image-page">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="h3 my-0 mx-0 text-foreground">Saved Images</h3>
        <p class="text-muted-foreground text-sm">Manage personalized employee images for review requests</p>
      </div>
      <div class="flex items-center space-x-4">
        <router-link
          :to="{ name: 'business.image-editor' }"
          class="btn round-xl px-4 create-new-image-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="plus-icon">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span class="text-sm font-medium">Create New Image</span>
        </router-link>
        <router-link
          :to="{
            name: 'request-reviews',
            params: {
              /* eslint-disable-next-line vue/camelcase */
              unique_id: user.business.unique_id + '-' + user.business.id,
            },
          }"
          class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
          target="_blank"
          ><i class="uil uil-star"></i
          ><span class="text-sm font-medium"> Request Review</span>
        </router-link>
      </div>
    </div>
    <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />

    <!-- Info Sections -->
    <div class="info-sections mb-4">
      <div class="info-card about-card">
        <div class="info-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="info-icon">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <h5 class="info-title">About Personalized Images</h5>
        </div>
        <p class="info-text">
          Boost review conversions by adding employee faces to review requests. Images are used in emails, texts, and social posts.
        </p>
      </div>
      <div class="info-card pro-tip-card">
        <div class="info-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lightbulb-icon">
            <path d="M9 21h6"></path>
            <path d="M12 3a6 6 0 0 0 6 6v8a6 6 0 0 1-6 6 6 6 0 0 1-6-6V9a6 6 0 0 0 6-6z"></path>
          </svg>
          <h5 class="info-title">Pro Tip</h5>
        </div>
        <p class="info-text">
          Use clear, high-quality photos with your logo or vehicle for maximum impact.
        </p>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="search-container mb-4">
      <div class="search-box">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="search-icon">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <b-form-input
          v-model="searchQuery"
          type="search"
          placeholder="Search employees by name or assignment..."
          class="search-input"
        ></b-form-input>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="!hasLoaded && !filteredImages.length" class="text-center pt-4">
      <b-spinner large />
    </div>

    <!-- Empty State -->
    <div v-else-if="!filteredImages.length" class="empty-state text-center py-5 bg-white rounded shadow">
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="empty-icon text-muted mb-3">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
        <circle cx="8.5" cy="8.5" r="1.5"></circle>
        <polyline points="21 15 16 10 5 21"></polyline>
      </svg>
      <h3 class="h5 font-weight-medium">No saved images</h3>
      <p class="text-muted">Get started by creating your first image</p>
      <div class="mt-3">
        <router-link
          :to="{ name: 'business.image-editor' }"
          class="btn bg-primary"
        >
          Create New Image
        </router-link>
      </div>
    </div>

    <!-- Image Cards -->
    <div v-else class="image-cards-grid">
      <div
        v-for="image in filteredImages"
        :key="image.id"
        class="image-card-new"
      >
        <div class="card-padding">
          <!-- Image Container -->
          <div class="image-container-new">
            <img
              :src="image.url"
              :alt="getEmployeeName(image) + '\'s personalized image'"
              class="employee-image-new"
            />
            <!-- Speech Bubble Overlay -->
            <div
              v-if="image.settings && image.settings.text"
              class="speech-bubble"
              :style="{
                '--bubble-color': image.settings.brandColor || '#FF6B35',
                backgroundColor: image.settings.brandColor || '#FF6B35',
                color: 'white',
                top: (image.settings.position?.y || 10) + '%',
                left: (image.settings.position?.x || 10) + '%',
              }"
            >
              {{ getSpeechBubbleText(image.settings.text) }}
            </div>
            <!-- Default Badge -->
            <div v-if="image.default" class="default-badge-new">
              Default
            </div>
          </div>

          <!-- Employee Details -->
          <div class="employee-details-new">
            <div class="details-grid">
              <div class="detail-item">
                <p class="detail-label-new">Employee Name</p>
                <p class="detail-value-new">{{ getEmployeeName(image) }}</p>
              </div>
              <div class="detail-item">
                <p class="detail-label-new">Assigned</p>
                <p class="detail-value-new">{{ getEmployeeName(image) }}</p>
              </div>
              <div class="detail-item">
                <p class="detail-label-new">Updated</p>
                <p class="detail-value-new">{{ formatDate(image.updated_at) }}</p>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons-new">
              <button
                class="action-btn-new"
                @click="handleEdit(image)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="btn-icon-new">
                  <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                </svg>
                Edit
              </button>
              <button
                class="action-btn-new"
                @click="handleRename(image)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="btn-icon-new">
                  <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                  <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                  <path d="M10 9H8"></path>
                  <path d="M16 13H8"></path>
                  <path d="M16 17H8"></path>
                </svg>
                Rename
              </button>
              <button
                class="action-btn-new"
                @click="handleAssign(image)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="btn-icon-new">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <polyline points="16 11 18 13 22 9"></polyline>
                </svg>
                Assign
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rename Modal -->
    <b-modal
      v-model="showRenameModal"
      title="Rename Image"
      header-bg-variant="light"
      ok-title="Save"
      ok-variant="primary"
      cancel-variant="outline-dark"
      @ok="saveNewName"
      @hidden="closeRenameModal"
    >
      <b-form-group>
        <b-form-input
          v-model="newImageName"
          placeholder="Enter new name"
          required
        ></b-form-input>
      </b-form-group>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Deletion"
      header-bg-variant="light"
      ok-title="Delete"
      ok-variant="danger"
      cancel-variant="outline-dark"
      @ok="confirmDelete"
      @hidden="closeDeleteModal"
    >
      <p class="mb-0">Are you sure you want to delete this image? This action cannot be undone.</p>
    </b-modal>

    <!-- Employee Assign Modal -->
    <b-modal
      v-model="showAssignModal"
      title="Assign to Employee"
      header-bg-variant="light"
      ok-title="Save"
      ok-variant="primary"
      cancel-variant="outline-dark"
      @ok="confirmAssign"
      @hidden="closeAssignModal"
    >
      <p v-if="selectedImage" class="mb-0">
        <span class="d-block mb-2">Choose an employee to assign this image to.</span>
        <b-form-select v-model="selectedImage.employee_id" class="form-control">
          <option :value="null">No Employee (Unassigned)</option>
          <option v-for="employee in employees" :key="employee.id" :value="employee.id">
            {{ employee.name }}
          </option>
        </b-form-select>
      </p>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { BSpinner, BModal, BFormGroup, BFormInput, BFormSelect } from 'bootstrap-vue'
import Vue from 'vue'
import '../../../design/custom/components/_dashboard.scss'

export default {
  name: 'PersonalizedImage',
  components: {
    BSpinner,
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
  },
  data() {
    return {
      searchQuery: '',
      showRenameModal: false,
      showDeleteModal: false,
      showAssignModal: false,
      selectedImage: null,
      newImageName: '',
    }
  },
  computed: {
    ...mapState('imageEditor', ['savedImages', 'hasLoaded']),
    user() {
      return this.$store.getters['auth/user']
    },
    employees() {
      return this?.user?.business?.employees || []
    },
    filteredImages() {
      if (!this.searchQuery.trim()) {
        return this.savedImages
      }
      const query = this.searchQuery.toLowerCase()
      return this.savedImages.filter((image) => {
        const employeeName = this.getEmployeeName(image).toLowerCase()
        const imageName = (image.name || '').toLowerCase()
        return employeeName.includes(query) || imageName.includes(query)
      })
    },
  },
  mounted() {
    if (this.user && this.user.business) {
      this.fetchSavedImages(this.user.business.id)
    }
  },
  methods: {
    ...mapActions('imageEditor', ['fetchSavedImages', 'deleteSavedImage', 'setDefaultSavedImage', 'assignEmployee']),
    ...mapMutations('imageEditor', ['SET_EDITING_IMAGE_ID']),

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      }).format(date)
    },

    getEmployeeName(image) {
      if (image.employee && image.employee.name) {
        return image.employee.name
      }
      return image.name || 'Unassigned'
    },

    getSpeechBubbleText(text) {
      if (!text) return ''
      // Replace placeholder with a sample name
      return text.replace(/\[FirstName\]/g, 'Jack').replace(/\[firstname\]/g, 'Jack')
    },

    handleEdit(image) {
      this.SET_EDITING_IMAGE_ID(image.id)
      this.$router.push({ name: 'business.image-editor', params: { id: image.id } })
    },

    handleRename(image) {
      this.selectedImage = image
      this.newImageName = image.name
      this.showRenameModal = true
    },

    closeRenameModal() {
      this.showRenameModal = false
      this.selectedImage = null
      this.newImageName = ''
    },

    async saveNewName() {
      if (!this.newImageName.trim()) {
        return
      }

      try {
        const payload = {
          business_id: this.user.business.id,
          image_id: this.selectedImage.id,
          name: this.newImageName,
        }
        this.$store
          .dispatch('imageEditor/saveImageName', payload)
          .then(() => {
            this.fetchSavedImages(this.user.business.id).then(() => {
              this.closeRenameModal()
            })
          })
          .catch((err) => {
            console.log(err)
            Vue.$toast.open({
              message: err.response?.data?.errors || 'Failed to update image name',
              type: 'error',
              duration: 5000,
            })
          })
      } catch (error) {
        console.error('Error updating image name:', error)
        Vue.$toast.open({
          message: 'Failed to update image name',
          type: 'error',
          duration: 5000,
        })
      }
    },

    handleAssign(image) {
      this.selectedImage = { ...image }
      if (!this.selectedImage.employee_id) {
        this.selectedImage.employee_id = null
      }
      this.showAssignModal = true
    },

    closeAssignModal() {
      this.showAssignModal = false
      this.selectedImage = null
    },

    async confirmAssign() {
      if (this.selectedImage) {
        this.selectedImage.isWorking = true
        try {
          await this.assignEmployee(this.selectedImage)
          this.closeAssignModal()
          this.fetchSavedImages(this.user.business.id)
        } catch (error) {
          console.error('Error Assigning Image', error)
          this.selectedImage.isWorking = false
          Vue.$toast.open({
            message: 'Error Assigning Image',
            type: 'error',
            duration: 5000,
          })
        }
      }
    },

    handleDelete(image) {
      if (image.default) {
        Vue.$toast.open({
          message: 'You cannot delete the default image',
          type: 'error',
          duration: 5000,
        })
        return
      }
      this.selectedImage = image
      this.showDeleteModal = true
    },

    closeDeleteModal() {
      this.showDeleteModal = false
      this.selectedImage = null
    },

    async confirmDelete() {
      if (this.selectedImage) {
        this.selectedImage.isDeleting = true
        try {
          await this.deleteSavedImage({ businessId: this.user.business.id, imageId: this.selectedImage.id })
          this.closeDeleteModal()
          Vue.$toast.open({
            message: 'Image Successfully Deleted',
            type: 'success',
            duration: 5000,
          })
          this.fetchSavedImages(this.user.business.id)
        } catch (error) {
          this.selectedImage.isDeleting = false
          console.error('Error deleting image:', error)
          Vue.$toast.open({
            message: 'Error Deleting Image',
            type: 'error',
            duration: 5000,
          })
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.personalized-image-page {
  min-height: 100vh;
  background: white;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}


.mb-4 {
  margin-bottom: 1.5rem;
}

// Header Styles
.h3 {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.2;
}

.text-foreground {
  color: #1f2937;
}

.text-muted-foreground {
  color: #6b7280;
  font-size: 0.875rem;
}

.text-sm {
  font-size: 0.875rem;
}

.font-medium {
  font-weight: 500;
}

// Create New Image Button
.create-new-image-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
    color: #374151;
    text-decoration: none;
  }

  .plus-icon {
    width: 20px;
    height: 20px;
  }
}

// Request Review Button
.request-review-btn {
  background-color: rgb(36, 99, 235) !important;
  color: #ffffff !important;
}

// Info Sections
.info-sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.info-card {
  padding: 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.about-card {
  background-color: white;
}

.pro-tip-card {
  background-color: #fef3c7;
  border-color: #fde68a;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.info-icon,
.lightbulb-icon {
  width: 20px;
  height: 20px;
  color: #3b82f6;
  flex-shrink: 0;
}

.lightbulb-icon {
  color: #f59e0b;
}

.info-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #1f2937;
}

.info-text {
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.5;
  margin: 0;
}

// Search Bar
.search-container {
  margin-bottom: 1.5rem;
}

.search-box {
  position: relative;
  max-width: 100%;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #9ca3af;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;

  &:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
}

// Empty State
.empty-state {
  padding: 3rem 1.5rem;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  opacity: 0.5;
}

// Image Cards Grid
.image-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.image-card-new {
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
}

.card-padding {
  padding: 0;
}

.image-container-new {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background-color: #f3f4f6;
  overflow: hidden;
}

.employee-image-new {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.speech-bubble {
  position: absolute;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 2;
  max-width: 80%;
  word-wrap: break-word;
  color: white;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 20px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid var(--bubble-color, #FF6B35);
  }
}

.default-badge-new {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid #10b981;
  padding: 0.125rem 0.625rem;
  font-weight: 600;
  transition: all 0.2s;
  background-color: #10b981;
  color: white;
  font-size: 0.75rem;
  z-index: 2;
}

.employee-details-new {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  text-align: center;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label-new {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
  margin: 0 0 0.25rem 0;
}

.detail-value-new {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.action-buttons-new {
  display: flex;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.action-btn-new {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  height: 2.25rem;
  border-radius: 0.375rem;
  padding: 0 0.75rem;
  flex: 1;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
    color: #1f2937;
  }

  &:focus-visible {
    outline: 2px solid transparent;
    outline-offset: 2px;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  .btn-icon-new {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    pointer-events: none;
    margin-right: 0.5rem;
  }
}
</style>

